import React, { useEffect, useRef, useState } from 'react';
import { Check, Globe2, Monitor, Moon, Settings2, Sun } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { useTheme } from '../contexts/ThemeContext';

const themeOptions = [
  { value: 'light', label: 'preferences.light', icon: Sun },
  { value: 'dark', label: 'preferences.dark', icon: Moon },
  { value: 'system', label: 'preferences.system', icon: Monitor },
];

export default function SitePreferences({ compact = false, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const CurrentThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <div className="site-preferences" ref={rootRef}>
      <button
        type="button"
        className="icon-button preference-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('preferences.title')}
      >
        {compact ? <Settings2 size={18} /> : <CurrentThemeIcon size={17} />}
        {!compact && <span>{lang}</span>}
      </button>

      {open && (
        <>
          <div
            className="preferences-backdrop"
            onPointerDown={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className={`preferences-panel preferences-panel--${align}`}
            role="dialog"
            aria-label={t('preferences.title')}
          >
          <div className="preferences-heading">
            <span>{t('preferences.title')}</span>
            <button
              type="button"
              className="plain-icon-button"
              onClick={() => setOpen(false)}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>

          <fieldset className="preferences-group">
            <legend>{t('preferences.theme')}</legend>
            <div className="preferences-options preferences-options--stacked">
              {themeOptions.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  className={theme === value ? 'preference-option is-selected' : 'preference-option'}
                  onClick={() => setTheme(value)}
                  aria-pressed={theme === value}
                >
                  <Icon size={17} />
                  <span>{t(label)}</span>
                  {theme === value && <Check className="preference-check" size={16} />}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="preferences-group">
            <legend>{t('preferences.language')}</legend>
            <div className="preferences-options">
              <button
                type="button"
                className={lang === 'RU' ? 'preference-option is-selected' : 'preference-option'}
                onClick={() => setLang('RU')}
                aria-pressed={lang === 'RU'}
              >
                <Globe2 size={17} />
                <span>{t('preferences.russian')}</span>
              </button>
              <button
                type="button"
                className={lang === 'EN' ? 'preference-option is-selected' : 'preference-option'}
                onClick={() => setLang('EN')}
                aria-pressed={lang === 'EN'}
              >
                <Globe2 size={17} />
                <span>{t('preferences.english')}</span>
              </button>
            </div>
          </fieldset>
          </div>
        </>
      )}
    </div>
  );
}
